import { pipelineVelocityData } from "@/mocks/reportsData";
import type { PipelineVelocityStage } from "@/mocks/reportsData";

const maxTimeHours = 148.8; // Negotiation = 6.2 days

const healthConfig: Record<PipelineVelocityStage["health"], { badge: string; bar: string; label: string }> = {
  good: { badge: "bg-green-50 text-green-700", bar: "bg-green-400", label: "Good" },
  average: { badge: "bg-amber-50 text-amber-700", bar: "bg-amber-400", label: "Average" },
  slow: { badge: "bg-red-50 text-red-600", bar: "bg-red-400", label: "Slow" },
  none: { badge: "bg-gray-50 text-gray-400", bar: "bg-gray-200", label: "—" },
};

interface Props {
  onExport?: () => void;
}

export default function PipelineVelocity({ onExport }: Props) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Pipeline Velocity</h3>
            <p className="text-xs text-gray-500 mt-0.5">Average time leads spend in each stage</p>
          </div>
          {onExport && (
            <button
              onClick={onExport}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
              title="Export CSV"
            >
              <i className="ri-download-line text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stage</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Time</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Leads In</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Leads Out</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Drop-off %</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Health</th>
            </tr>
          </thead>
          <tbody>
            {pipelineVelocityData.map((row) => {
              const cfg = healthConfig[row.health];
              const barPct = row.avgTimeHours > 0 ? (row.avgTimeHours / maxTimeHours) * 100 : 0;

              return (
                <tr key={row.stage} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-3 text-xs font-medium text-gray-900 whitespace-nowrap">{row.stage}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-700 w-14 text-right flex-shrink-0">{row.avgTime}</span>
                      <div className="flex-1 min-w-[60px]">
                        {barPct > 0 && (
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${cfg.bar} rounded-full transition-all duration-500`}
                              style={{ width: `${Math.max(barPct, 2)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{row.leadsIn.toLocaleString()}</td>
                  <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                    {row.leadsOut !== null ? row.leadsOut.toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                    {row.dropOff || "—"}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Insight box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-alert-line text-amber-600" />
        </div>
        <p className="text-sm text-amber-800">
          <strong>Proposal Sent</strong> and <strong>Negotiation</strong> stages are taking longest. Consider adding follow-up automation for leads stuck more than 3 days.
        </p>
      </div>
    </div>
  );
}